import { useContext } from '@berlin-front/common/composables/context';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { ApiCacheContext } from '@services/ApiCacheContext';
import { ERROR_TYPE, SERVICE_TYPE, MODAL_MODE } from '@services/ModalParameterContext';
import { onBeforeUnmount, Ref, watch, computed, reactive, ref, nextTick } from '@vue/composition-api';
import { defaultTheme, ThemeContext } from '@berlin-front/common/services/ThemeContext';
import useHelp from '@berlin-front/common/composables/useHelp';
import { event } from 'vue-gtag';
import {
  isAxiosError,
  camelCaseDeep,
  notNil,
  required,
  formatNumber,
  snakeCaseDeep,
  omit,
  zodErrorToMessage,
} from '@berlin-front/common/utils';
import { responseInterceptorFactory } from '@/services/BerlinBff';
import { ROUTE_NAME } from '@/router/route-name';
import { broadcast } from '@/broadcast';
import { AMOUNT_DISPLAY_TYPE, UNIT_MAPPER } from '@/entity/amountDisplayType';
import {
  AtoneCapturePostResponseAuthorizationResultEnum,
  AtoneReauthorizePutResponseAuthorizationResultEnum,
} from '@netprotections/berlin-bff';
import useClientConnection from '@berlin-front/common/composables/useClientConnection';
import { atoneSettlementServiceParameterValidator } from '@schemas/atone-settlement/AtoneSettlementParameterSchema';
import { atoneSettlementCallbackDefinition } from '@schemas/atone-settlement/AtoneSettlementCallbackSchema';
import { legacyAtoneSettlementCallbackDefinition } from '@schemas/atone-settlement/LegacyAtoneSettlementCallbackSchema';
import { builtinCallbackDefinition } from '@schemas/common-schemas/builtin-callback-schema';
import { CamelCasedPropertiesDeep } from 'type-fest';
import { useErrorHandle } from '@composables/useErrorHandle';
import useIsLegacy from '@composables/useIsLegacy';
import useSpacer from '@composables/useSpacer';
import commonParamAdditionalSpace from '@schemas/common-schemas/common-param-additional-space';
import type { RegisterFormDefault, ServiceGlobals, ServiceDiscriminator } from './index';
import { isLegacyParam, usePrepaymentCheckHandle, useShopOption } from './atone-common';

/**
 * このファイルでハンドリングするサービスタイプ。（Atone）
 */
const CURRENT_SERVICE_TYPE = SERVICE_TYPE.ATONE;

/**
 * このファイルでハンドルするモーダルモード（決済モーダル）
 */
const CURRENT_MODE = MODAL_MODE.SETTLEMENT;

/**
 * このファイルでハンドルするエラー画面のルート名。
 */
const CURRENT_ERROR_ROUTE_NAME = ROUTE_NAME.ERROR_VIEW;

/**
 * バリデータ
 */
const VALIDATOR = atoneSettlementServiceParameterValidator;

/**
 * axiosのエラーレスポンスインターセプタ。
 *
 * shopOptionGetが失敗した際、エラー処理を遅延処理するために使う。
 */
const onShopOptionError = notNil(responseInterceptorFactory()[1]);

const isCurrentService = (param: unknown): boolean =>
  VALIDATOR.pick({ service_type: true, modal_mode: true }).safeParse(param).success;

const useBridge = (currentService: Ref<ServiceDiscriminator>) => {
  const [isLegacyRef, setIsLegacy] = useIsLegacy();
  /**
   * 初期化済みフラグ。
   *
   * init呼び出しに成功したらtrueとなり、失敗したらfalseとなる。
   */
  const initializedRef = ref(false);

  /**
   * 未整形の状態のpayment
   *
   * pre_paymentチェックと、JSErrorApiにぶん投げるのに使う。
   */
  const rawPaymentRef = ref<(typeof VALIDATOR._input)['payment'] | undefined>(undefined);

  /**
   * バリデータを通過し、キャメルに変換されたパラメータ。
   */
  let normalizedParameter: CamelCasedPropertiesDeep<typeof VALIDATOR._output> | undefined;

  /**
   * 店舗公開鍵
   */
  const publicKeyRef = ref<string | undefined>(undefined);

  const router = useRouter();
  const [shopOptionGet, shopOptionPromiseRef] = useShopOption({
    meta: { service_type: CURRENT_SERVICE_TYPE, modal_mode: CURRENT_MODE },
  });

  /**
   * コールバック実行基盤
   */
  const connection = useClientConnection();
  const {
    atoneShopV2OptionGetResult,
    atoneReauthorizePutResult,
    atoneCapturePostResult,
    atoneSplitCapturePostResult,
    atoneModalParameter,
    setAtoneModalParameter,
    setAtoneShopV2OptionGetResult,
    tokens,
    setNpTokenAndSignature,
    signature,
  } = useContext(ApiCacheContext);
  const { setThemeParameter } = useContext(ThemeContext);

  /**
   * トークンの変動を監視するかどうか。
   *
   * startするタイミングでsetするpre_tokenでは監視を止めるようにうまい具合に切り替える。
   */
  const watchNpToken = ref(false);

  // モーダルが終了したタイミングでnpTokenの監視を停止する。
  watch([currentService], ([cs]) => {
    if (!cs) {
      watchNpToken.value = false;
    }
  });

  /**
   * ヘッダに表示する金額の単位
   */
  const headerTotalPriceUnit = computed(() => {
    // TODO: v2移行 amountDisplayTypeの変更内容を反映させる
    const amountDisplayType =
      atoneShopV2OptionGetResult.value?.atoneServiceOption.amountDisplayType || AMOUNT_DISPLAY_TYPE.HIDE;
    if (amountDisplayType in UNIT_MAPPER) {
      return UNIT_MAPPER[amountDisplayType as keyof typeof UNIT_MAPPER];
    }
    return UNIT_MAPPER[AMOUNT_DISPLAY_TYPE.HIDE];
  });

  /**
   * ヘッダに表示する金額。
   */
  const headerTotalPrice = computed(() => {
    if (headerTotalPriceUnit && initializedRef.value && atoneModalParameter.value) {
      return formatNumber(atoneModalParameter.value.payment.amount);
    }
    return '未確定';
  });

  /**
   * 会員登録フォームのデフォルト値設定
   */
  const registerFormDefault = computed<RegisterFormDefault>(() =>
    initializedRef.value
      ? {
          birthday: atoneModalParameter.value.payment.customer?.birthday ?? '',
          email: atoneModalParameter.value.payment.customer?.email ?? '',
          phoneNumber: atoneModalParameter.value.payment.customer?.phoneNumber ?? '',
          address: atoneModalParameter.value.payment.customer?.address ?? '',
          customerFamilyName: atoneModalParameter.value.payment.customer?.customerFamilyName ?? '',
          customerGivenName: atoneModalParameter.value.payment.customer?.customerGivenName ?? '',
          customerFamilyNameKana: atoneModalParameter.value.payment.customer?.customerFamilyNameKana ?? '',
          customerGivenNameKana: atoneModalParameter.value.payment.customer?.customerGivenNameKana ?? '',
          sexDivision: atoneModalParameter.value.payment.customer?.sexDivision ?? '3',
          zipCode: atoneModalParameter.value.payment.customer?.zipCode ?? '',
        }
      : {},
  );
  const headerShopName = computed(() => atoneShopV2OptionGetResult.value?.shopName || '');
  const pubkey = computed(() => (initializedRef.value ? atoneModalParameter.value.pubKey : ''));
  const userNo = computed(() => (initializedRef.value ? atoneModalParameter.value.payment.userNo : undefined));
  const requestId = computed(() => atoneModalParameter.value?.requestId);
  const help = useHelp();
  const serviceGlobals: Ref<ServiceGlobals | undefined> = computed(() =>
    initializedRef.value
      ? {
          ...reactive({
            requestId,
            serviceType: CURRENT_SERVICE_TYPE,
            errorRouteName: CURRENT_ERROR_ROUTE_NAME,
            pubkey,
            headerShopName,
            headerTotalPrice,
            headerTotalPriceUnit,
            registerFormDefault,
            userNo,
          }),
          ...help,
        }
      : undefined,
  );

  const isActive = computed(() => isCurrentService(currentService.value));

  // 余白コントロール
  const additionalSpaceRef = ref({ top: 0, bottom: 0 });
  useSpacer({
    isActiveRef: isActive,
    additionalSpaceRef,
  });

  // エラーハンドル処理。
  // APIエラーとJSエラーをハンドルして、コールバックとJSErrorAPIの実行をやってくれる。
  useErrorHandle({
    activeService: { service_type: CURRENT_SERVICE_TYPE, modal_mode: CURRENT_MODE },
    activeRef: isActive,
    errorRouteName: CURRENT_ERROR_ROUTE_NAME,
    paymentRef: rawPaymentRef,
    tokensRef: tokens,
    publicKeyRef,
    errorCallback: (param) => {
      if (isLegacyRef.value) {
        void connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'error', [
          param.name,
          param.message,
          param.errors,
        ]);
      } else {
        void connection.executeCallback(atoneSettlementCallbackDefinition, 'error', [
          {
            service_type: '01',
            modal_mode: '01',
            ...param,
          },
        ]);
      }
    },
  });

  // プリペイメントコールバック処理
  // コールバックが呼べない時はshopエラーになる。
  usePrepaymentCheckHandle({
    activeRef: isActive,
    errorRouteName: CURRENT_ERROR_ROUTE_NAME,
    callback: async (): Promise<boolean> => {
      if (isLegacyRef.value) {
        return connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'pre_payment', [
          rawPaymentRef.value,
        ]);
      }
      return connection.executeCallback(atoneSettlementCallbackDefinition, 'pre_payment', [
        {
          service_type: '01',
          modal_mode: '01',
          payment: rawPaymentRef.value,
        },
      ]);
    },
  });

  // その他のブロードキャストイベントの処理。
  // 画面で発火したイベントをもとにコールバック読んだりするためのルールなんか記述する。
  // エラーハンドル処理はほぼ共通化できるのでそれだけ分離してる。
  const unlistenList = [
    // モーダル終了イベント（決済完了）
    broadcast.listen('MODAL_CONTROL_SUCCEEDED', () => {
      if (!isCurrentService(currentService.value)) return;

      let authorizationSuccessResponseData;
      if (atoneReauthorizePutResult.value) {
        authorizationSuccessResponseData = atoneReauthorizePutResult.value;
      } else if (atoneCapturePostResult.value) {
        authorizationSuccessResponseData = atoneCapturePostResult.value;
      } else {
        authorizationSuccessResponseData = notNil(atoneSplitCapturePostResult.value);
      }
      // 処理結果が存在し、結果が1(決済成功)なら、success処理を行う。そうでない場合はランタイムエラー（実装が間違ってる）。
      // istanbul ignore else
      if (
        authorizationSuccessResponseData?.authorizationResult ===
        AtoneCapturePostResponseAuthorizationResultEnum.NUMBER_1
      ) {
        // TODO: swaggerが間違ってるのでrequiredにしてもらう。
        const toRequiredResult = required(authorizationSuccessResponseData);
        const [companyCode, shopBranchCode, transactionId] = toRequiredResult.transactionCode.split('-');
        if (isLegacyRef.value) {
          // 旧Atoneコールバック。仕様書項目以外にも結構余計なものを返してるので、その通りに返却。
          void connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'succeeded', [
            {
              ...omit(
                snakeCaseDeep(toRequiredResult),
                'shop_name',
                'transaction_code',
                'callback_authorization_result',
                'callback_authorization_result_ng_reason',
              ),
              shop_transaction_no: atoneModalParameter.value.payment.shopTransactionNo,
            },
          ]);
        } else {
          // 統合インターフェイスコールバック
          void connection.executeCallback(atoneSettlementCallbackDefinition, 'succeeded', [
            {
              service_type: '01',
              modal_mode: '01',
              authorization_result: toRequiredResult.callbackAuthorizationResult,
              company_code: companyCode,
              shop_branch_code: shopBranchCode,
              transaction_id: transactionId,
              shop_transaction_no: notNil(atoneModalParameter.value.payment.shopTransactionNo),
            },
          ]);
        }
        void connection.executeCallback(builtinCallbackDefinition, 'close', []).then(() => {
          void router.replace({ name: ROUTE_NAME.HOME });
        });
      }
    }),
    // モーダル終了イベント（決済失敗）
    broadcast.listen('MODAL_CONTROL_FAILED', (payload) => {
      if (!isCurrentService(currentService.value)) return;
      // システムエラーの場合はnoResult
      if (payload.noResult) {
        if (isLegacyRef.value) {
          void connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'failed', [undefined]);
        } else {
          void connection.executeCallback(atoneSettlementCallbackDefinition, 'failed', [
            {
              service_type: '01',
              modal_mode: '01',
            },
          ]);
        }
      } else {
        // reAuthorize優先で決済処理結果が存在すれば取得する。
        let authorizationSuccessResponseData;
        if (atoneReauthorizePutResult.value) {
          authorizationSuccessResponseData = atoneReauthorizePutResult.value;
        } else if (atoneCapturePostResult.value) {
          authorizationSuccessResponseData = atoneCapturePostResult.value;
        } else {
          authorizationSuccessResponseData = notNil(atoneSplitCapturePostResult.value);
        }
        if (
          authorizationSuccessResponseData?.authorizationResult !==
          AtoneReauthorizePutResponseAuthorizationResultEnum.NUMBER_1
        ) {
          // TODO: swaggerが間違ってるのでrequiredにしてもらう。
          const toRequiredResult = required(authorizationSuccessResponseData);
          const { callbackAuthorizationResultNgReason, callbackAuthorizationResult } = toRequiredResult;
          const [companyCode, shopBranchCode, transactionId] = toRequiredResult.transactionCode.split('-');
          if (isLegacyRef.value) {
            void connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'failed', [
              {
                ...omit(
                  snakeCaseDeep(toRequiredResult),
                  'shop_name',
                  'transaction_code',
                  'callback_authorization_result',
                  'callback_authorization_result_ng_reason',
                ),
                shop_transaction_no: notNil(atoneModalParameter.value.payment.shopTransactionNo),
              },
            ]);
          } else {
            void connection.executeCallback(atoneSettlementCallbackDefinition, 'failed', [
              {
                service_type: '01',
                modal_mode: '01',
                authorization_result: callbackAuthorizationResult,
                authorization_result_ng_reason: callbackAuthorizationResultNgReason,
                company_code: companyCode,
                shop_branch_code: shopBranchCode,
                transaction_id: transactionId,
                shop_transaction_no: notNil(atoneModalParameter.value.payment.shopTransactionNo),
              },
            ]);
          }
        }
      }
      void connection.executeCallback(builtinCallbackDefinition, 'close', []).then(() => {
        void router.replace({ name: ROUTE_NAME.HOME });
      });
    }),
    // モーダル終了イベント（xボタンによる離脱）
    broadcast.listen('MODAL_CONTROL_CANCELLED', () => {
      if (!isCurrentService(currentService.value)) return;
      if (isLegacyRef.value) {
        void connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'cancelled', []);
      } else {
        void connection.executeCallback(atoneSettlementCallbackDefinition, 'cancelled', [
          {
            service_type: '01',
            modal_mode: '01',
          },
        ]);
      }
      void connection.executeCallback(builtinCallbackDefinition, 'close', []).then(() => {
        void router.replace({ name: ROUTE_NAME.HOME });
      });
      window.newrelic.addPageAction('close_modal');
    }),
    // モーダル終了イベント（カート不整合エラー）
    broadcast.listen('MODAL_CONTROL_INVALID', () => {
      if (!isCurrentService(currentService.value)) return;
      if (isLegacyRef.value) {
        void connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'invalid', []);
      } else {
        void connection.executeCallback(atoneSettlementCallbackDefinition, 'invalid', [
          {
            service_type: '01',
            modal_mode: '01',
          },
        ]);
      }
      void connection.executeCallback(builtinCallbackDefinition, 'close', []).then(() => {
        void router.replace({ name: ROUTE_NAME.HOME });
      });
      window.newrelic.addPageAction('close_invalid_modal');
    }),
  ] as const;
  onBeforeUnmount(() => unlistenList.forEach((unlisten) => unlisten()));

  // トークン更新を通知。
  watch(
    () => tokens.value?.npToken,
    (token) => {
      if (
        !isCurrentService(currentService.value) || // サービスタイプが一致する時。
        !watchNpToken.value || // preTokenセット時には反応しないように。
        !token // トークンのクリアには反応しない。
      )
        return;

      if (token && token !== atoneModalParameter.value.preToken) {
        if (isLegacyRef.value) {
          void connection.executeCallback(legacyAtoneSettlementCallbackDefinition, 'authenticated', [
            token,
            atoneModalParameter.value.payment.userNo,
          ]);
        } else {
          void connection.executeCallback(atoneSettlementCallbackDefinition, 'authenticated', [
            {
              service_type: '01',
              modal_mode: '01',
              authentication_token: token,
              ...(signature.value && { signature: signature.value }),
              user_no: atoneModalParameter.value.payment.userNo,
            },
          ]);
        }
      }
    },
  );

  /**
   * service_typeとmodal_modeをチェックする。
   * @param rawParameter
   * @returns
   */
  const check: (rawParameter: unknown) => boolean = isCurrentService;

  /**
   * 初期化リクエストメッセージハンドラ
   *
   * ショップサイトからのメッセージをハンドリングする3つのハンドラの一つ。
   *
   * 開始前の初期データ設定。
   *
   * - 初期化処理として事前に呼び出し可能なAPIなどを実行しておき、モーダル起動処理時間を短縮する。
   * - 初期化が終わったらREADYメッセージを応答すること。
   *
   * ショップサイト側のモジュールはREADYイベントが発生するまでは他のメッセージを保留（キューに溜め置き待機）する。
   *
   * @param parameter
   * @returns
   */
  const init: (rawParameter: unknown) => void = (rawParameter) => {
    // 生の決済パラメータはlog用で、まっさきに保存する。
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rawPaymentRef.value = (rawParameter as any)?.payment ?? {};

    // バリデートする前にレガシー判定を行います。
    // 先に判定しておかないと、バリデートの失敗時に、レガシーのエラーコールバックを呼ぶべきか統合インターフェイスのエラーコールバックを呼ぶべきかわからないため。
    setIsLegacy(isLegacyParam(rawParameter));

    // 余白指定を取り込む
    const additionalSpaceParseResult = commonParamAdditionalSpace.safeParse(rawParameter);
    if (additionalSpaceParseResult.success) {
      additionalSpaceRef.value = additionalSpaceParseResult.data.additional_space;
    }

    // バリデート。
    const parseResult = VALIDATOR.safeParse(rawParameter);
    if (!parseResult.success) {
      initializedRef.value = false;
      const { name, stack } = parseResult.error;
      const message = zodErrorToMessage(parseResult.error);
      // エラーを通知
      broadcast.send({
        type: 'JS_ERROR',
        payload: {
          service: {
            service_type: CURRENT_SERVICE_TYPE,
            modal_mode: CURRENT_MODE,
          },
          name,
          message,
          stack,
        },
      });
      return;
    }
    const parameter = parseResult.data;
    publicKeyRef.value = parameter.pub_key;

    normalizedParameter = camelCaseDeep(parameter);
    // パラメタをキャッシュする。
    setAtoneModalParameter(normalizedParameter);
    shopOptionGet(normalizedParameter.pubKey, normalizedParameter.payment.transactionOptions ?? []);
    // GA4イベント送信
    event('service_type', { service_type: 'Atone' });

    // 初期化済みにする。
    initializedRef.value = true;
  };

  /**
   * 開始リクエストメッセージハンドラ
   *
   * ショップサイトからのメッセージをハンドリングする3つのハンドラの一つ。
   * 決済モーダルの起動メッセージを処理する。
   * @param parameter
   * @returns
   */
  const start: (rawParameter: unknown) => void = (rawParameter) => {
    if (!isCurrentService(rawParameter)) return;

    // モーダル開く時に現在のサービスタイプを設定する。ここでisActiveがtrueになるため最優先で設定する。
    currentService.value = { service_type: CURRENT_SERVICE_TYPE, modal_mode: CURRENT_MODE };

    // 一旦デフォルトテーマを設定。
    setThemeParameter(defaultTheme());

    if (!initializedRef.value || !shopOptionPromiseRef.value) {
      broadcast.send({
        type: 'JS_ERROR',
        payload: {
          service: {
            service_type: CURRENT_SERVICE_TYPE,
            modal_mode: CURRENT_MODE,
          },
          name: 'RequestException',
          message: '未初期化、もしくは不正なパラメータが与えられた状態で処理が開始されました。',
        },
      });
      void router.replace({ name: CURRENT_ERROR_ROUTE_NAME, params: { errorType: 'shop' } });
      void connection.executeCallback(builtinCallbackDefinition, 'show', []);
      return;
    }

    void shopOptionPromiseRef.value.then(async (shopOptionResponse) => {
      setAtoneShopV2OptionGetResult(shopOptionResponse);
      if (atoneShopV2OptionGetResult.value?.atoneServiceOption.errors) {
        void connection.executeCallback(atoneSettlementCallbackDefinition, 'error', [
          {
            service_type: CURRENT_SERVICE_TYPE,
            modal_mode: CURRENT_MODE,
            ...ERROR_TYPE.REQUEST_EXCEPTION,
            errors: atoneShopV2OptionGetResult.value.atoneServiceOption.errors,
          },
        ]);
        void router.replace({ name: CURRENT_ERROR_ROUTE_NAME, params: { errorType: 'shop' } });
        void connection.executeCallback(builtinCallbackDefinition, 'show', []);
        return;
      }
      // UIテーマを設定する。
      // TODO: shopOptionGetがテーマに対応するのを待つ。
      setThemeParameter(defaultTheme());

      // pre_tokenをセット。セットする時authenticatedが実行されないようにゴニョってる。
      if (normalizedParameter?.preToken) {
        setNpTokenAndSignature(normalizedParameter?.preToken);
        // npTokenの監視をスタート。
        await nextTick();
      }

      watchNpToken.value = true;

      // 画面遷移。
      await router.replace({ name: ROUTE_NAME.ATONE_LOADING });

      // 表示を指示。
      void connection.executeCallback(builtinCallbackDefinition, 'show', []);

      // newrelicに通知。
      if (!normalizedParameter) return;
      window.newrelic.addPageAction('modal_activation', {
        revenue: normalizedParameter.payment.amount,
        shop_transaction_no: normalizedParameter.payment.shopTransactionNo,
        affiliation: atoneShopV2OptionGetResult.value?.shopName ?? '',
        products: snakeCaseDeep(normalizedParameter.payment.items),
      });
      window.newrelic.setCustomAttribute('shop_name', atoneShopV2OptionGetResult.value?.shopName ?? '');
      window.newrelic.setCustomAttribute('amount', normalizedParameter.payment.amount);
      window.newrelic.setCustomAttribute('shop_transaction_no', normalizedParameter.payment.shopTransactionNo);
      window.newrelic.setCustomAttribute('items', snakeCaseDeep(normalizedParameter.payment.items));
    });
    void shopOptionPromiseRef.value.catch((e) => {
      // UIテーマを設定する。
      // shopOptionGetが失敗したとき、デフォルトテーマしか設定できない。
      setThemeParameter(defaultTheme());
      // エラー種別に応じて遷移する。
      if (isAxiosError(e)) {
        // 発生したエラーを再度axiosインターセプタに送る。
        // サービス開始後なので、エラー画面に遷移する。
        // TODO: 再度JSErrorApi呼ばれちゃうのでほんとは直したい。
        onShopOptionError(e);
        // モーダルを表示。
        void connection.executeCallback(builtinCallbackDefinition, 'show', []);
      } else {
        broadcast.send({
          type: 'JS_ERROR',
          payload: {
            service: {
              service_type: CURRENT_SERVICE_TYPE,
              modal_mode: CURRENT_MODE,
            },
            name: 'RequestException',
            message: e instanceof Error ? e.message : String(e),
          },
        });
        void router.replace({ name: CURRENT_ERROR_ROUTE_NAME, params: { errorType: 'shop' } });
        // 表示を指示。
        void connection.executeCallback(builtinCallbackDefinition, 'show', []);
      }
    });
  };

  // ハンドラーを返却する。
  return {
    init,
    start,
    check,
    serviceGlobals,
    serviceDiscriminator: { service_type: CURRENT_SERVICE_TYPE, modal_mode: CURRENT_MODE },
  };
};

export default useBridge;
